import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Archive, CircleDot } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

/**
 * Gestão das origens do lead. Mesma forma da seção de motivos de perda — as
 * duas são listas do funil configuradas pelo mesmo perfil.
 */
export function DealSourcesSection() {
  const listQuery = trpc.crm.sources.list.useQuery({});
  const utils = trpc.useUtils();

  // Invalida a lista em todas as telas que mostram o rótulo da origem.
  const refresh = () => utils.crm.sources.list.invalidate();

  const create = trpc.crm.sources.create.useMutation({
    onSuccess: () => {
      refresh();
      toast.success("Origem adicionada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao adicionar origem")),
  });
  const update = trpc.crm.sources.update.useMutation({
    onSuccess: refresh,
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao renomear origem")),
  });
  const archive = trpc.crm.sources.archive.useMutation({
    onSuccess: () => {
      refresh();
      toast.success("Origem arquivada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao arquivar origem")),
  });

  const [newName, setNewName] = useState("");
  const rows = listQuery.data ?? [];

  function add() {
    const name = newName.trim();
    if (!name) return;
    create.mutate({ name, position: rows.length }, { onSuccess: () => setNewName("") });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CircleDot className="h-4 w-4" /> Origens do lead
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          De onde a oportunidade veio. Aparecem ao criar uma oportunidade e no
          filtro da lista.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2">
          <Input
            placeholder="Nova origem (ex: Instagram)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
            className="flex-1"
          />
          <Button onClick={add} disabled={create.isPending || !newName.trim()}>
            <Plus className="h-4 w-4 mr-1" />
            Adicionar
          </Button>
        </div>
        {rows.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground border-2 border-dashed rounded-lg mt-2">
            Nenhuma origem cadastrada.
          </div>
        ) : (
          <div className="space-y-1.5 pt-1">
            {rows.map((s: any) => (
              <div
                key={s.id}
                className="group flex items-center gap-2 border rounded-md px-3 py-2 hover:bg-muted/30"
              >
                <CircleDot className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input
                  className="h-8 flex-1 border-transparent shadow-none focus:border-input focus:shadow-sm"
                  defaultValue={s.name}
                  onBlur={(e) => {
                    const name = e.target.value.trim();
                    if (name && name !== s.name) update.mutate({ id: s.id, name });
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100"
                  onClick={() => archive.mutate({ id: s.id })}
                  title="Arquivar"
                >
                  <Archive className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground pt-1">
          Arquivar tira a origem das novas oportunidades, mas as que já usavam
          continuam mostrando o nome.
        </p>
      </CardContent>
    </Card>
  );
}
